import { useState } from "react";
import { Search, Plus, X, QrCode, AlertTriangle, Loader2 } from "lucide-react";

const initials = (t) =>
  (t || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";

const fmtDate = (iso) => {
  try {
    return new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Berlin" }).format(new Date(iso));
  } catch { return ""; }
};

// Linke WhatsApp-artige Liste: alle zugaenglichen Standorte (flach).
// Header: Anzahl + Plus-Button zum Anlegen. Anlegen erfolgt unter einem
// uebergeordneten Objekt (Parent); bei mehreren Parents kann gewaehlt werden.
export function LiegenschaftList({ items, parents = [], activePermId, onSelect, onCreate, onAushang }) {
  const [q, setQ] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [aushangBusy, setAushangBusy] = useState(null);
  const creatable = parents.filter((p) => p.can_create);
  const [parentPermId, setParentPermId] = useState("");
  const limitedParents = parents.filter((p) => Number.isFinite(p.max_locations));

  const query = q.trim().toLowerCase();
  const filtered = query
    ? items.filter((i) => `${i.title || ""} ${i.address || ""} ${i.parent_title || ""} ${i.perm_id}`.toLowerCase().includes(query))
    : items;

  const openCreate = () => { setParentPermId(creatable[0]?.perm_id || ""); setName(""); setShowCreate(true); };

  const save = async () => {
    const t = name.trim();
    const pid = parentPermId || creatable[0]?.perm_id;
    if (!t || !onCreate || !pid) return;
    setSaving(true);
    try {
      await onCreate(t, pid);
      setName("");
      setShowCreate(false);
    } catch { /* Fehler wird vom Aufrufer via Toast gemeldet */ }
    finally { setSaving(false); }
  };

  const handleAushang = async (row) => {
    if (!onAushang || aushangBusy) return;
    setAushangBusy(row.perm_id);
    try { await onAushang(row); } finally { setAushangBusy(null); }
  };

  const selectedParent = creatable.find((p) => p.perm_id === (parentPermId || creatable[0]?.perm_id));
  const parentTitle = selectedParent?.title;
  const selMax = selectedParent && Number.isFinite(selectedParent.max_locations) ? selectedParent.max_locations : null;
  const selCount = Number(selectedParent?.location_count) || 0;
  const selFull = selMax != null && selCount >= selMax;

  return (
    <div className="flex flex-col h-full" data-testid="liegenschaft-list">
      <div className="p-3" style={{ borderBottom: "1px solid #E2E8F0", background: "#fff" }}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-extrabold uppercase" style={{ color: "#64748B", letterSpacing: ".04em" }}>Standorte</span>
            {limitedParents.length === 0 && (
              <span className="text-xs font-bold px-2 py-0.5 rounded-md" style={{ background: "#F1F5F9", color: "#64748B" }} data-testid="liegenschaft-count">{items.length}</span>
            )}
            {limitedParents.map((p) => {
              const cur = Number(p.location_count) || 0;
              const full = cur >= p.max_locations;
              return (
                <span key={p.perm_id} data-testid={`liegenschaft-limit-${p.perm_id}`}
                  title={`${p.title || "Standorte"}: ${cur} von ${p.max_locations} Standorten belegt`}
                  className="text-xs font-bold px-2 py-0.5 rounded-md"
                  style={{ background: full ? "#FEF2F2" : "#FFF7ED", color: full ? "#B91C1C" : "#C2410C", border: `1px solid ${full ? "#FECACA" : "#FED7AA"}` }}>
                  {limitedParents.length > 1 ? `${p.title}: ` : ""}{cur}
                </span>
              );
            })}
          </div>
          {onCreate && creatable.length > 0 && (
            <button data-testid="liegenschaft-add-open" onClick={openCreate}
              title="Neue Standort anlegen"
              className="w-7 h-7 rounded-lg flex items-center justify-center"
              style={{ background: "#F97316", border: "none", cursor: "pointer" }}>
              <Plus size={16} color="#fff" />
            </button>
          )}
        </div>
        <div style={{ position: "relative" }}>
          <Search size={15} style={{ position: "absolute", left: 10, top: 11, color: "#94A3B8" }} />
          <input data-testid="liegenschaft-search" className="input-wd text-sm" style={{ paddingLeft: 32 }}
            placeholder="Standort suchen…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto" style={{ background: "#F0F2F5" }}>
        {filtered.length === 0 && (
          <p className="text-sm p-4" style={{ color: "#64748B" }} data-testid="liegenschaft-empty">
            {items.length === 0 ? "Keine zugänglichen Standorte." : "Kein Treffer."}
          </p>
        )}
        {filtered.map((row) => {
          const isActive = row.perm_id === activePermId;
          const open = Number(row.open_count) || 0;
          const newCount = Number(row.new_count) || 0;
          const isNew = newCount > 0;
          return (
            <div key={row.perm_id} data-testid={`liegenschaft-row-${row.perm_id}`} role="button" onClick={() => onSelect(row)}
              className="flex items-center gap-3 px-3 py-3"
              style={{ cursor: "pointer", borderBottom: "1px solid #E4E9EF", background: isActive ? "#FFF7ED" : "#fff", borderLeft: isActive ? "4px solid #F97316" : "4px solid transparent" }}>
              <span className="rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm"
                style={{ width: 44, height: 44, background: "#FFEAD6", color: "#C2410C" }}>{initials(row.title)}</span>
              <div className="min-w-0 flex-1">
                <div className="font-bold text-sm truncate flex items-center gap-1.5" data-testid={`liegenschaft-title-${row.perm_id}`}>
                  {isNew && (
                    <span data-testid={`liegenschaft-unread-dot-${row.perm_id}`} title="Neue/ungelesene Meldung"
                      className="inline-block rounded-full flex-shrink-0" style={{ width: 9, height: 9, background: "#DC2626" }} />
                  )}
                  {row.title}
                  {row.error && (
                    <span title={row.error} data-testid={`liegenschaft-error-${row.perm_id}`}
                      className="inline-flex items-center gap-0.5 text-xs font-bold px-1.5 py-0.5 rounded" style={{ background: "#FEF2F2", color: "#B91C1C" }}>
                      <AlertTriangle size={11} /> Rechte
                    </span>
                  )}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                {row.latest_modified && (
                  <span className="text-xs" data-testid={`liegenschaft-latest-${row.perm_id}`} style={{ color: isNew ? "#DC2626" : "#94A3B8", fontWeight: isNew ? 700 : 400 }}>{fmtDate(row.latest_modified)}</span>
                )}
                <div className="flex items-center gap-1.5">
                  {newCount > 0 ? (
                    <span data-testid={`liegenschaft-new-count-${row.perm_id}`} title={`${newCount} neue, ungesehene Meldung(en)`}
                      className="rounded-full flex items-center justify-center font-bold text-xs"
                      style={{ minWidth: 22, height: 22, padding: "0 6px", background: "#DC2626", color: "#fff" }}>{newCount}</span>
                  ) : open > 0 ? (
                    <span data-testid={`liegenschaft-open-count-${row.perm_id}`} title={`${open} offen · bereits gesehen`}
                      className="rounded-full flex items-center justify-center font-bold text-xs"
                      style={{ minWidth: 22, height: 22, padding: "0 6px", background: "#94A3B8", color: "#fff" }}>{open}</span>
                  ) : null}
                  {onAushang && (row.can_create || row.can_manage) && (
                    <button data-testid={`aushang-pdf-${row.perm_id}`} onClick={() => handleAushang(row)} disabled={aushangBusy === row.perm_id}
                      title="PDF-Aushang mit QR-Code herunterladen"
                      className="w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{ background: "#F1F5F9", border: "none", cursor: aushangBusy === row.perm_id ? "wait" : "pointer" }}>
                      {aushangBusy === row.perm_id ? <Loader2 size={15} className="animate-spin" color="#64748B" /> : <QrCode size={15} color="#64748B" />}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {showCreate && (
        <div className="modal-overlay" data-testid="liegenschaft-create-modal" onClick={() => !saving && setShowCreate(false)}>
          <div className="modal-card" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <div>
                <h3 className="text-lg font-extrabold m-0">Neue Standort</h3>
                {parentTitle && <p className="text-xs m-0 mt-1" style={{ color: "#64748B" }}>unter „{parentTitle}"</p>}
              </div>
              <button className="modal-close" data-testid="liegenschaft-create-close" onClick={() => !saving && setShowCreate(false)}><X size={20} /></button>
            </div>
            <div style={{ padding: 24 }}>
              {creatable.length > 1 && (
                <div className="mb-3">
                  <label className="text-xs font-bold" style={{ color: "#475569" }}>Übergeordnetes Objekt</label>
                  <select data-testid="liegenschaft-parent-select" className="input-wd mt-1" value={parentPermId}
                    onChange={(e) => setParentPermId(e.target.value)}>
                    {creatable.map((p) => <option key={p.perm_id} value={p.perm_id}>{p.title || p.perm_id}</option>)}
                  </select>
                </div>
              )}
              <label className="text-xs font-bold" style={{ color: "#475569" }}>Name der Standort</label>
              <input data-testid="liegenschaft-name-input" className="input-wd mt-1" autoFocus
                placeholder="z. B. Standort Nord" value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !selFull) save(); }} />
              {selMax != null && (
                <p className="text-xs m-0 mt-2" data-testid="liegenschaft-limit-hint"
                  style={{ color: selFull ? "#B91C1C" : "#64748B", fontWeight: selFull ? 700 : 400 }}>
                  {selFull
                    ? `Standortlimit erreicht (${selCount} / ${selMax}). Es kann kein weiterer Standort angelegt werden.`
                    : `${selCount} / ${selMax} Standorte belegt.`}
                </p>
              )}
              <div className="flex items-center justify-end gap-2 mt-5">
                <button data-testid="liegenschaft-create-cancel" className="btn-secondary" disabled={saving} onClick={() => setShowCreate(false)}>Abbrechen</button>
                <button data-testid="liegenschaft-create-save" className="btn-primary" disabled={saving || !name.trim() || selFull} onClick={save}>
                  {saving ? "Speichern…" : "Speichern"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
